import { useCallback, useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { LoadingScreen, MainContent, Subhead } from '@growtherapy/sprout-ui';

import { useIsAutomaticAdmissionEnabled } from '../../../hooks/useAdmissionFlow';
import { shortUuid } from '../../../utils';
import { usePrimeAudioAutoPlayAction } from '../../../video-sdk/actions/useLifecycleActions';
import { useEnterWaitingRoomCallback } from '../../nav/useEnterWaitingRoomCallback';
import { usePIIVerification } from '../required-agreements/usePIIVerification';
import { useRequiredAgreements } from '../required-agreements/useRequiredAgreements';
import { FooterContent } from './FooterContent';
import { ProviderAvatarWrapper as NewProviderAvatar } from './ProviderAvatarWrapper';
import { RightContent } from './RightContent';
import { useIntroRoomState } from './useIntroRoomState';
import { useVisitorName } from './useVisitorName';

export type IntroRoomContentProps = {
  rightContent: React.ReactNode;
  footerContent?: React.ReactNode;
  providerAvatarSectionComponent: typeof NewProviderAvatar;
  title: string;
};

/**
 * Presentational component for the intro room content. This UI does NOT support
 * the pre-rebrand design system.
 */
export function IntroRoomContent({
  providerAvatarSectionComponent: ProviderAvatarComponent,
  rightContent,
  footerContent,
  title,
}: IntroRoomContentProps) {
  return (
    <div className='w-full h-full flex flex-col'>
      <div className='grow sm:py-12 sm:px-14 overflow-y-auto grid [@media(min-height:1000px)]:place-items-center'>
        <MainContent variant='twoColumnWithHeader' className='max-w-[1400px]'>
          <MainContent.Header>
            <span className='hidden sm:block'>{title}</span>
          </MainContent.Header>
          <MainContent.Left>
            <Subhead.Large
              as='h1'
              className='text-center rebrand:text-2xlarge rebrand:text-neutral-900 sm:hidden mb-11 px-350'
            >
              {title}
            </Subhead.Large>
            <ProviderAvatarComponent />
          </MainContent.Left>
          <MainContent.Right>{rightContent}</MainContent.Right>
        </MainContent>
      </div>
      {footerContent}
    </div>
  );
}

/**
 * Wrapper component for the intro room content. This UI does NOT support the
 * pre-rebrand design system.
 */
export function IntroRoomContentWrapper() {
  const t = useTranslate();
  // Unique ID for a single PII verification and consent signing attempt, to
  // link Segment events to a single "trial" of the flow.
  const [flowId, setFlowId] = useState<string>(shortUuid());
  const resetFlowId = useCallback(() => {
    setFlowId(shortUuid());
  }, []);

  const { introRoomState, onPIIVerified } = useIntroRoomState(flowId);

  const { visitorNameIsRequired, visitorName, visitorNameInput, setVisitorName, setAccountDataVisitorName } =
    useVisitorName();
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const primeAudioAutoPlay = usePrimeAudioAutoPlayAction();
  const isAutomaticAdmissionEnabled = useIsAutomaticAdmissionEnabled();
  const onEnterWaitingRoom = useCallback(async () => {
    // prime audio autoplay within the entry gesture; only needed for the
    if (isAutomaticAdmissionEnabled) await primeAudioAutoPlay();
    enterWaitingRoom(visitorName);
  }, [isAutomaticAdmissionEnabled, primeAudioAutoPlay, enterWaitingRoom, visitorName]);

  // ---- For default state ----
  const [enterIsDisabled, setEnterIsDisabled] = useState(false);

  // ---- For PII verification state ----
  const {
    formState: piiVerificationFormState,
    handleSubmit: handlePIIVerificationSubmit,
    isSubmitDisabled: isSubmitPIIVerificationDisabled,
    isSubmitting: isSubmittingPIIVerification,
    responseData: {
      consentSigningToken,
      serverError: piiVerificationServerError,
      verificationData: piiVerificationData,
    },
    setFormState: setPIIVerificationFormState,
    setFormIsValid: setPIIVerificationFormIsValid,
  } = usePIIVerification();

  const onPIIVerificationSubmit = useCallback(async () => {
    const success = await handlePIIVerificationSubmit(t, flowId);
    if (!success) {
      // If the PII verification fails, reset the flow ID to indicate the client
      // has started a new "trial" of the flow.
      resetFlowId();
      return;
    }
    onPIIVerified();
  }, [t, flowId, handlePIIVerificationSubmit, onPIIVerified, resetFlowId]);

  // ---- For required agreements review state ----
  const {
    hasSubmissionError,
    isSubmitDisabled,
    isSubmitting,
    shouldSeeSeparateAccountCallout,
    ...requiredAgreementsProps
  } = useRequiredAgreements({
    consentSigningToken,
    flowId,
    onSigningSuccess: onEnterWaitingRoom,
    piiVerificationData,
    setAccountDataVisitorName,
  });

  if (introRoomState === 'loading') {
    return <LoadingScreen />;
  }

  let title: string;
  let rightContent: React.ReactNode;
  let footerContent: React.ReactNode;
  switch (introRoomState) {
    case 'piiVerification':
      title = t('telehealth-core:introRoom.letsVerifyYourIdentity');
      rightContent = (
        <RightContent.PIIVerification
          formState={piiVerificationFormState}
          onFormStateChange={setPIIVerificationFormState}
          onIsValidChange={setPIIVerificationFormIsValid}
          onSubmit={onPIIVerificationSubmit}
          serverError={piiVerificationServerError}
        />
      );
      footerContent = (
        <FooterContent.PIIVerification
          isDisabled={isSubmitPIIVerificationDisabled}
          isSubmitting={isSubmittingPIIVerification}
          onSubmit={onPIIVerificationSubmit}
        />
      );
      break;

    case 'requiredAgreementsReview':
      title = t('telehealth-core:introRoom.letsGetReady');
      rightContent = (
        <RightContent.RequiredAgreements
          hasSubmissionError={hasSubmissionError}
          isRequired={visitorNameIsRequired}
          onEnterIsDisabledChange={setEnterIsDisabled}
          requiredAgreementsProps={requiredAgreementsProps}
          setVisitorName={setVisitorName}
          shouldSeeSeparateAccountCallout={shouldSeeSeparateAccountCallout}
          visitorName={visitorNameInput}
        />
      );
      footerContent = (
        <FooterContent.RequiredAgreementsReview
          isDisabled={isSubmitDisabled}
          isSubmitting={isSubmitting}
          onSubmit={requiredAgreementsProps.onSubmit}
        />
      );
      break;

    default:
      title = t('telehealth-core:introRoom.letsGetReady');
      rightContent = (
        <RightContent.Default
          isRequired={visitorNameIsRequired}
          onEnterIsDisabledChange={setEnterIsDisabled}
          onEnterWaitingRoom={onEnterWaitingRoom}
          setVisitorName={setVisitorName}
          visitorName={visitorNameInput}
        />
      );
      footerContent = <FooterContent.Default isDisabled={enterIsDisabled} onEnterWaitingRoom={onEnterWaitingRoom} />;
      break;
  }

  return (
    <IntroRoomContent
      title={title}
      providerAvatarSectionComponent={NewProviderAvatar}
      rightContent={rightContent}
      footerContent={footerContent}
    />
  );
}
